import { RecordFolderAssignment } from '@provision/entity-types';
import { PageRoots, portalPaths } from '@provision/portal-routes';
import { ReactNode, useMemo } from 'react';
import { isEmpty, isNullish } from 'remeda';
import { type IconLibrary } from 'src/components/DynamicIcon/dynamic-icon';
import { EllipsesMenuOption } from 'src/components/EllipsesMenu/ellipses-menu';
import { useWorkspaceMenuItems } from 'src/components/Navbar/use-workspace-menu-items';
import { filterItemsByAccess, mapAssignmentToNavItem } from 'src/components/Navbar/utils';
import {
  AllowedRuleSet,
  MatchType,
  PartnerGroupRules,
  PartnerSetting,
  RecordFolder,
  useCurrentUserProfile,
  useGroupRules,
  usePartnerSettings,
  UserGroupRules,
  useUserGroupRuleValues,
  useViewRecordFolders,
} from 'src/graphql';
import { CustomViewNavItem, useCustomViewMenuItems } from 'src/graphql/entities/custom-view';
import { RecordFolderVisibility } from 'src/graphql/entities/record-folder';
import { useDeleteRecordFolderAssignment } from 'src/graphql/entities/record-folder-assignment';
import { hasWorkflowManagementAccess } from 'src/graphql/entities/workflow';
import { SETTINGS_NAVIGATION_GROUPS } from 'src/pages';
import { useGlobalStore } from 'src/store';
import useNavigationStore from 'src/store/navigation';
import { useNavigateRoute } from 'src/utils/routing';
import { trimDuplicatedSlashes } from 'src/utils/routing/use-navigate-route';
import { useScreenDimensions } from 'src/utils/system';

export interface NavigationDataItem {
  id: string;
  title: string;
  iconData: { name: string; filled?: boolean; library?: IconLibrary };
  path: string;
  allowedRules?: (PartnerGroupRules | UserGroupRules | AllowedRuleSet)[];
  disallowedRules?: (PartnerGroupRules | UserGroupRules)[];
  disallowedPartnerSettings?: PartnerSetting[];
  matchType?: MatchType;
  isMultitenancyAllowed?: boolean;
  isMobileSupported?: boolean;
  customViewId?: string;
  menuItems?: EllipsesMenuOption[];
  rawAssignmentItem?: RecordFolderAssignment;
  pathTemplate?: string;
  // path to take data from
  badgeDataPath?: string;
  // data that was already taken by the path
  badgeContent?: string;
  hideWithNoCount?: boolean;
}

export interface NavigationGroup {
  id: string;
  title: string;
  icon?: string;
  items: NavigationDataItem[];
  onClick?: (item: NavigationDataItem) => void;
  onDelete?: (item: NavigationDataItem) => void;
  onReorder?: (items: CustomViewNavItem[]) => void;
  onShowHidden?: () => void;
  shouldShowHiddenItems?: boolean;
  rightAdornment?: ReactNode;
  enableFolderCreation?: boolean;
  folder?: RecordFolder;
  hiddenGroups?: NavigationGroup[];
  isHidden?: boolean;
  allowEmpty?: boolean;
}

const EMPTY_NAVIGATION_GROUPS: NavigationGroup[] = [];

interface NavigationItems {
  navigationGroups: NavigationGroup[];
  settingsNavigationGroups: NavigationGroup[];
  landingRoute: string | null;
  loading: boolean;
}

export function useNavigationItems(): NavigationItems {
  const { user } = useCurrentUserProfile();
  const { partnerPrefix, loading: urlIsLoading } = useNavigateRoute();
  const { loading, checkValidity, groupRules } = useGroupRules();
  const { userGroupRules } = useUserGroupRuleValues();
  const { isMobile } = useScreenDimensions();
  const isMultiTenancyMode = useGlobalStore.use.isMultiTenancyMode();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const { shouldShowHiddenFolders, setShowHiddenFolders } = useNavigationStore();

  const { viewsMenuItems, customViews, reorderViews } = useCustomViewMenuItems();
  const { recordFolders, isEnabled, reorderAssignments } = useViewRecordFolders();
  const { deleteRecordFolderAssignment } = useDeleteRecordFolderAssignment();
  const { data: partnerSettings } = usePartnerSettings({ partnerId: selectedPartnerId });

  const isWorkingWithPartner = !isMultiTenancyMode || !isNullish(selectedPartnerId);

  const workspaceMenuItems = useWorkspaceMenuItems();

  const navigationGroups = useMemo<NavigationGroup[] | null>(() => {
    if (loading || isEmpty(groupRules)) {
      return null;
    }

    const visibleFolders = recordFolders?.filter(
      (folder) =>
        (folder.assignments?.length || folder.userId === user?.id) &&
        (shouldShowHiddenFolders || folder.visibility === RecordFolderVisibility.Shown),
    );

    const groups: NavigationGroup[] = [
      {
        id: 'favorites',
        title: 'Favorites',
        items: viewsMenuItems,
        onReorder: reorderViews,
        shouldShowHiddenItems:
          recordFolders?.length && !visibleFolders?.length
            ? shouldShowHiddenFolders
            : undefined,
        onShowHidden: () => {
          setShowHiddenFolders(!shouldShowHiddenFolders);
        },
      },
    ];

    if (recordFolders?.length) {
      visibleFolders.forEach((folder, index) => {
        const isOwn = folder.userId === user?.id;

        const visibleItems = folder.assignments
          ?.map((assignment) =>
            mapAssignmentToNavItem({
              assignment,
              customViews,
              folder,
              selectedPartnerId,
              deleteAssignment:
                (folder.isPrivate && isOwn) ||
                checkValidity([UserGroupRules.AccessToManagePublicFolders])
                  ? deleteRecordFolderAssignment
                  : undefined,
            }),
          )
          .filter((item) => Boolean(item.path));

        if (visibleItems?.length || isOwn) {
          groups.push({
            id: folder.id,
            icon: folder?.isPrivate && isOwn ? 'lock' : '',
            title: folder.name,
            isHidden: folder.visibility === RecordFolderVisibility.Hidden,
            onReorder: reorderAssignments,
            shouldShowHiddenItems:
              index === visibleFolders.length - 1 ? shouldShowHiddenFolders : undefined,
            onShowHidden: () => {
              setShowHiddenFolders(!shouldShowHiddenFolders);
            },
            items: filterItemsByAccess({
              partnerSettings,
              items: visibleItems ?? [],
              checkValidity,
              isWorkingWithPartner,
              isMobile,
              selectedPartnerId,
            }),
            folder,
            enableFolderCreation: index === visibleFolders.length - 1 && isEnabled,
            allowEmpty: true,
          });
        }
      });
    }

    groups.push({
      id: 'workspace',
      title: 'Workspace',
      items: workspaceMenuItems,
      enableFolderCreation: isEnabled,
    });

    return groups
      .map((group) => ({
        ...group,
        items: filterItemsByAccess({
          partnerSettings,
          items: group.items,
          checkValidity,
          isWorkingWithPartner,
          isMobile,
          selectedPartnerId,
        }),
      }))
      .filter((group) => group.allowEmpty || group.items?.length);
  }, [
    loading,
    workspaceMenuItems,
    groupRules,
    viewsMenuItems,
    customViews,
    reorderViews,
    recordFolders,
    reorderAssignments,
    shouldShowHiddenFolders,
    selectedPartnerId,
    deleteRecordFolderAssignment,
    setShowHiddenFolders,
    checkValidity,
    isWorkingWithPartner,
    isMobile,
    user?.id,
    isEnabled,
    partnerSettings,
  ]);

  const settingsNavigationGroups = useMemo<NavigationGroup[] | null>(() => {
    if (loading || isEmpty(groupRules)) {
      return null;
    }

    return SETTINGS_NAVIGATION_GROUPS.map((group) => ({
      id: group.id,
      title: group.title,
      items: filterItemsByAccess({
        partnerSettings,
        items: group.items.filter(
          (item) =>
            item.path !== portalPaths.settings.workflows() ||
            hasWorkflowManagementAccess(userGroupRules),
        ),
        checkValidity,
        isWorkingWithPartner,
        isMobile,
        selectedPartnerId,
      }),
    })).filter((group) => group.items.length);
  }, [
    loading,
    groupRules,
    userGroupRules,
    partnerSettings,
    checkValidity,
    isWorkingWithPartner,
    isMobile,
    selectedPartnerId,
  ]);

  const allNavigationItems = useMemo(() => {
    if (!navigationGroups) {
      return null;
    }
    return [...navigationGroups, ...(settingsNavigationGroups ?? [])].flatMap(
      (group) => group.items,
    );
  }, [navigationGroups, settingsNavigationGroups]);

  const landingRoute = useMemo(() => {
    if (!allNavigationItems || urlIsLoading) {
      return null;
    }

    /**
     * Application starting page is determined by user's access rights and multitenancy mode.
     * 'All Conversations' is a default route, but if they have no Conversations access,
     * then we redirect to CRM page.
     * If no menu items are available, because of restrictions imposed by conditions above,
     * we redirect the user to 404 page.
     */

    const conversationsItem = allNavigationItems.find(
      (item) =>
        item.path ===
        portalPaths.entity.all({
          entityName: 'conversations',
        }),
    );

    if (conversationsItem) {
      return conversationsItem.path;
    }

    const crmOrganizations = allNavigationItems.find(
      (item) => item.path === portalPaths.crm.organizations(),
    );

    if (crmOrganizations) {
      return crmOrganizations.path;
    }

    if (allNavigationItems.length) {
      return allNavigationItems[0].path;
    }

    return PageRoots.notFound;
  }, [allNavigationItems, urlIsLoading]);

  return {
    navigationGroups: navigationGroups || EMPTY_NAVIGATION_GROUPS,
    settingsNavigationGroups: settingsNavigationGroups || EMPTY_NAVIGATION_GROUPS,
    landingRoute: landingRoute
      ? trimDuplicatedSlashes(`${partnerPrefix}/${landingRoute}`)
      : null,
    loading,
  };
}
